import type {
  AIActionKey,
  CustomerActionAllowanceSnapshot,
  PlanSet,
  PlanTier,
  SubscriptionCatalog,
  SubscriptionSnapshot,
} from '@tabbio/types';

type CreditUsageLabels = {
  loadingPlan: string;
  creditUsageUnavailable: string;
  planLabels?: Partial<Record<PlanSet, Partial<Record<PlanTier, string>>>>;
  planDescriptions?: Partial<Record<PlanSet, Partial<Record<PlanTier, string>>>>;
};

export type SubscriptionCreditUsageDisplay = {
  planSet: PlanSet;
  tier: PlanTier;
  planLabel: string;
  planDescription: string;
  isLoading: boolean;
  hasCreditUsage: boolean;
  unavailableLabel: string | null;
  monthlyLimit: number | null;
  monthlyUsed: number;
  monthlyRemaining: number | null;
  monthlyPercentUsed: number | null;
  monthlyPercentRemaining: number | null;
  monthlyResetAt: string | null;
  dailyLimit: number | null;
  dailyUsed: number;
  dailyRemaining: number | null;
  dailyPercentUsed: number | null;
  dailyPercentRemaining: number | null;
  dailyResetAt: string | null;
  actionAllowances: CustomerActionAllowanceSnapshot[];
};

export function getCustomerAllowanceLabelKey(actionKey: AIActionKey) {
  switch (actionKey) {
    case 'individual.career_agent_chat':
      return 'settings.allowances.careerChat';
    case 'individual.skill_run':
      return 'settings.allowances.skillRuns';
    case 'individual.nova_compile':
      return 'settings.allowances.novaPreviews';
    case 'individual.cv_section_preview':
      return 'settings.allowances.cvPreviews';
    case 'cv_fit_report_generate':
      return 'settings.allowances.cvFitReports';
    default:
      return 'settings.allowances.aiActions';
  }
}

function clampPercent(value: number) {
  if (!Number.isFinite(value)) {
    return null;
  }

  return Math.max(0, Math.min(100, value));
}

function getUsedPercent(used: number, reserved: number, limit: number | null) {
  if (limit === null || limit <= 0) {
    return null;
  }

  return clampPercent(((used + reserved) / limit) * 100);
}

function getRemaining(
  explicitRemaining: number | null | undefined,
  limit: number | null,
  used: number,
  reserved: number,
) {
  if (explicitRemaining !== null && explicitRemaining !== undefined) {
    return Math.max(explicitRemaining, 0);
  }

  if (limit === null) {
    return null;
  }

  return Math.max(limit - used - reserved, 0);
}

function getRemainingPercent(remaining: number | null, limit: number | null) {
  if (remaining === null || limit === null || limit <= 0) {
    return null;
  }

  return clampPercent((remaining / limit) * 100);
}

function getLocalizedPlanCopy(
  labels: Partial<Record<PlanSet, Partial<Record<PlanTier, string>>>> | undefined,
  planSet: PlanSet,
  tier: PlanTier,
  fallback: string,
) {
  return labels?.[planSet]?.[tier] ?? fallback;
}

export function buildSubscriptionCreditUsageLabels(
  t: (key: string) => string,
): CreditUsageLabels {
  return {
    loadingPlan: t('settings.checkingSubscription'),
    creditUsageUnavailable: t('settings.creditUsageUnavailable'),
    planLabels: {
      individual: {
        free: t('settings.subscriptionPlans.tiers.free'),
        plus: t('settings.subscriptionPlans.tiers.plus'),
        pro: t('settings.subscriptionPlans.tiers.pro'),
      },
      employer: {
        free: t('settings.subscriptionPlans.tiers.free'),
        plus: t('settings.subscriptionPlans.tiers.plus'),
        pro: t('settings.subscriptionPlans.tiers.pro'),
      },
    },
    planDescriptions: {
      individual: {
        free: t('settings.subscriptionPlans.descriptions.individual.free'),
        plus: t('settings.subscriptionPlans.descriptions.individual.plus'),
        pro: t('settings.subscriptionPlans.descriptions.individual.pro'),
      },
      employer: {
        free: t('settings.subscriptionPlans.descriptions.employer.free'),
        plus: t('settings.subscriptionPlans.descriptions.employer.plus'),
        pro: t('settings.subscriptionPlans.descriptions.employer.pro'),
      },
    },
  };
}

/**
 * `locale` defaults to en-US for the existing callers; the usage screen passes
 * `i18n.language` so Arabic gets its own digits and separators. `null` means
 * no limit: callers that show it to people pass a translated label instead.
 */
export function formatCreditAmount(
  value: number | null | undefined,
  locale: string = 'en-US',
) {
  if (value === null || value === undefined) {
    return 'Unlimited';
  }

  const normalized = Math.max(value, 0);
  if (normalized > 0 && normalized < 1) {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(normalized);
  }

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: normalized < 100 ? 1 : 0,
  }).format(normalized);
}

export function selectSubscriptionCreditUsageDisplay(input: {
  catalog: SubscriptionCatalog;
  snapshot: SubscriptionSnapshot | null;
  isLoading: boolean;
  labels: CreditUsageLabels;
  workspaceMode?: PlanSet | 'seeker';
}): SubscriptionCreditUsageDisplay {
  const planSet =
    input.snapshot?.planSet ??
    (input.workspaceMode === 'employer' ? 'employer' : 'individual');
  const tier = input.snapshot?.tier ?? input.catalog.planSets[planSet].defaultTier;
  const plan = input.catalog.planSets[planSet].tiers[tier];
  const isLoading = input.isLoading && !input.snapshot;
  const usage = input.snapshot?.usage ?? null;
  const credits = usage?.credits ?? null;
  const dailyLimit =
    credits?.dailyLimit ??
    plan.limits.aiCreditsPerDay ??
    plan.limits.aiActionsPerDay ??
    null;
  const monthlyLimit =
    credits?.cycleLimit ?? plan.limits.aiCreditsPerCycle ?? null;
  const dailyUsed = credits?.dailyUsed ?? usage?.used ?? 0;
  const dailyReserved = credits?.dailyReserved ?? usage?.reserved ?? 0;
  const monthlyUsed = credits?.cycleUsed ?? dailyUsed;
  const monthlyReserved = credits?.cycleReserved ?? dailyReserved;
  const monthlyRemaining = getRemaining(
    credits?.cycleRemaining,
    monthlyLimit,
    monthlyUsed,
    monthlyReserved,
  );
  const dailyRemaining = getRemaining(
    credits?.dailyRemaining ?? usage?.remaining,
    dailyLimit,
    dailyUsed,
    dailyReserved,
  );
  const hasCreditUsage =
    planSet === 'individual' && (dailyLimit !== null || monthlyLimit !== null);
  const actionAllowances = Object.values(
    input.snapshot?.actionAllowances ?? {},
  ).sort((left, right) => left.actionKey.localeCompare(right.actionKey));

  return {
    planSet,
    tier,
    planLabel: isLoading
      ? input.labels.loadingPlan
      : getLocalizedPlanCopy(input.labels.planLabels, planSet, tier, plan.label),
    planDescription: getLocalizedPlanCopy(
      input.labels.planDescriptions,
      planSet,
      tier,
      plan.marketing.highlight ?? plan.description,
    ),
    isLoading,
    hasCreditUsage,
    unavailableLabel: hasCreditUsage ? null : input.labels.creditUsageUnavailable,
    monthlyLimit,
    monthlyUsed,
    monthlyRemaining,
    monthlyPercentUsed: getUsedPercent(monthlyUsed, monthlyReserved, monthlyLimit),
    monthlyPercentRemaining: getRemainingPercent(monthlyRemaining, monthlyLimit),
    monthlyResetAt: credits?.cycleResetAt ?? input.snapshot?.renewsAt ?? null,
    dailyLimit,
    dailyUsed,
    dailyRemaining,
    dailyPercentUsed: getUsedPercent(dailyUsed, dailyReserved, dailyLimit),
    dailyPercentRemaining: getRemainingPercent(dailyRemaining, dailyLimit),
    dailyResetAt: credits?.dailyResetAt ?? usage?.resetAt ?? null,
    actionAllowances,
  };
}
